// assets/popup-add-to-cart.js
(() => {
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));

  // ---- DOM refs
  const getPopupRoot = () => qs(".popup-add-to-cart-sticky");
  const getImgEl = () => qs(".popup-add-to-cart__inner img");
  const getVarEl = () => qs(".popup-add-to-cart__inner-content-variant");
  const getDefaultVarEl = () =>
    qs(
      ".popup-add-to-cart__inner-content-variant[data-popup-variant-default-label]"
    );
  const getTitleEl = () => qs(".popup-add-to-cart__inner-content-title");
  const getPriceEl = () => qs(".popup-add-to-cart__inner-content-price");
  const getCloseBtn = () => qs(".popup-add-to-cart__header span");
  const getBagBtn = () => qs(".popup-add-to-cart__footer-button");
  const getContLink = () => qs(".popup-add-to-cart__footer-link");

  const openPopup = () => getPopupRoot()?.classList.add("active");
  const closePopup = () => getPopupRoot()?.classList.remove("active");

  // ---- currency icon toggle (root or per-button fallback)
  let lastClickIconPref = null; // "true" | "false" | null
  const readBoolAttr = (el, name) => {
    if (!el) return null;
    const v = el.getAttribute(name);
    return v == null ? null : String(v).toLowerCase();
  };
  const useCurrencyIcon = () => {
    const rootPref = readBoolAttr(getPopupRoot(), "data-popup-currency-icon");
    return (rootPref ?? lastClickIconPref ?? "false") === "true";
  };

  // ---- helpers
  const formatAmountNoZeros = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return String(amount.toFixed(2)).replace(/\.?0+$/, "");
    }
  };

  const renderCustomCurrencyToken = (container, cents) => {
    if (!container) return;
    container.innerHTML = "";

    const token = document.createElement("span");
    token.className = "custom-currency custom-currency--width";
    token.setAttribute("data-money", String(cents));
    token.setAttribute("data-cents", "true");
    if (window.Shopify?.currency?.active) {
      token.setAttribute("data-currency", Shopify.currency.active);
    }

    const cur = window.Shopify?.currency?.active;
    const fallback = (() => {
      try {
        if (cur) {
          return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: cur,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Number(cents) / 100);
        }
      } catch {}
      return `$${(Number(cents) / 100).toFixed(2).replace(/\.?0+$/, "")}`;
    })();
    token.textContent = fallback;

    container.appendChild(token);

    try {
      window.CustomCurrency?.refresh?.();
    } catch {}
    try {
      document.dispatchEvent(new CustomEvent("custom-currency:refresh"));
    } catch {}
    try {
      window.dispatchEvent(new Event("resize"));
    } catch {}
  };

  // ---- Fill popup from /cart/add.js response
  const populatePopup = (item) => {
    const image = item?.image || item?.featured_image?.url || null;

    const imgEl = getImgEl();
    if (imgEl) {
      if (image) imgEl.src = image;
      imgEl.alt = item?.product_title || item?.title || "Product image";
    }

    const varEl = getVarEl();
    const defaultVarEl = getDefaultVarEl();
    if (varEl)
      varEl.textContent =
        item?.variant_title ||
        defaultVarEl.dataset.popupVariantDefaultLabel ||
        "";

    const titleEl = getTitleEl();
    if (titleEl) titleEl.textContent = item?.product_title || item?.title || "";

    const priceCents =
      item?.final_price ?? item?.price ?? item?.discounted_price ?? 0;
    const priceEl = getPriceEl();

    if (useCurrencyIcon()) {
      if (priceEl) {
        priceEl.innerHTML = "";
        priceEl.textContent = formatAmountNoZeros(priceCents);
      }
    } else {
      renderCustomCurrencyToken(priceEl, priceCents);
    }
  };

  // ---- Cart bubble update (flicker-free)
  const updateCartBubble = async (addedQty = 1) => {
    // 1) Preferred: theme event (CartIcon listens here)
    const evtName = window?.ThemeEvents?.cartUpdate;
    if (typeof evtName === "string") {
      const detail = {
        data: {
          itemCount: Number(addedQty) || 1,
          source: "product-form-component",
        },
      };
      document.dispatchEvent(new CustomEvent(evtName, { detail }));
      return; // stop here to avoid double paths
    }

    // 2) Direct component method (no fetch; component will handle sessionStorage)
    const cartIconEl = document.querySelector("cart-icon");
    if (cartIconEl && typeof cartIconEl.renderCartBubble === "function") {
      cartIconEl.renderCartBubble(
        Number(addedQty) || 1,
        true,
        /* animate */ true
      );
      return;
    }

    // 3) DOM fallback — fetch total and also sync sessionStorage so ensureCartBubbleIsCorrect won't rewind it
    try {
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );
      const total = Number(cart?.item_count || 0);

      const bubble =
        document.querySelector('[ref="cartBubble"]') ||
        document.querySelector(".cart-bubble");
      const countEl =
        document.querySelector('[ref="cartBubbleCount"]') ||
        document.querySelector(".cart-bubble__text-count");
      const textWrap =
        document.querySelector('[ref="cartBubbleText"]') ||
        document.querySelector(".cart-bubble__text");

      if (bubble && countEl) {
        countEl.textContent = total < 100 ? String(total) : "";
        countEl.classList.toggle("hidden", total === 0);
        bubble.classList.toggle("visually-hidden", total === 0);
        bubble.classList.toggle("cart-bubble--animating", total > 0);
        // end animation after it runs
        if (textWrap) {
          // simple timeout fallback; your theme uses onAnimationEnd internally
          setTimeout(
            () => bubble.classList.remove("cart-bubble--animating"),
            600
          );
        } else {
          setTimeout(
            () => bubble.classList.remove("cart-bubble--animating"),
            600
          );
        }
      }

      // Write sessionStorage exactly like the component so later checks don't revert the count
      try {
        sessionStorage.setItem(
          "cart-count",
          JSON.stringify({ value: String(total), timestamp: Date.now() })
        );
      } catch {}

      // (No extra custom events here to avoid double handling)
    } catch (e) {
      console.warn("Cart bubble DOM fallback failed", e);
    }
  };

  // ---- AJAX add
  const addToCart = async (payload) => {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });
    if (!res.ok)
      throw new Error(await res.text().catch(() => `Add failed ${res.status}`));
    return res.json();
  };

  // ---- Build payload
  const payloadFromBtn = (btn) => {
    const form = btn.closest("form");
    if (form) {
      const fd = new FormData(form);
      const id = fd.get("id") || btn.getAttribute("data-variant-id");
      const quantity = Number(fd.get("quantity") || 1) || 1;

      const properties = {};
      fd.forEach((v, k) => {
        if (k.startsWith("properties[") && k.endsWith("]")) {
          properties[k.slice(11, -1)] = v;
        }
      });

      if (!id)
        throw new Error(
          'Missing variant id (input[name="id"] or data-variant-id).'
        );
      return {
        id: String(id),
        quantity,
        ...(Object.keys(properties).length ? { properties } : {}),
      };
    }

    const id = btn.getAttribute("data-variant-id");
    if (!id)
      throw new Error(
        'Missing variant id. Add hidden input[name="id"] or data-variant-id.'
      );
    return { id: String(id), quantity: 1 };
  };

  // ---- Click handler
  const onClickATC = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;

    // capture per-click currency pref
    const btnPref = readBoolAttr(btn, "data-popup-currency-icon");
    lastClickIconPref = btnPref;

    let payload;
    try {
      payload = payloadFromBtn(btn);
    } catch (err) {
      console.warn(err?.message || err);
      return;
    }

    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");

    try {
      const item = await addToCart(payload);
      await updateCartBubble(payload.quantity || 1); // pass added qty
      populatePopup(item);
      openPopup();
    } catch (err) {
      console.error("Add to cart error:", err);
    } finally {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
    }
  };

  // ---- Wire up
  const bindButtons = () => {
    qsa("[data-popup-add-to-cart]").forEach((btn) => {
      if (btn.__popupBound) return;
      btn.addEventListener("click", onClickATC);
      btn.__popupBound = true;
    });
  };

  const initPopup = () => {
    getBagBtn()?.setAttribute("href", "/cart");
    getCloseBtn()?.addEventListener("click", (e) => {
      e.preventDefault();
      closePopup();
    });

    const root = getPopupRoot();
    root?.addEventListener("click", (e) => {
      if (e.target === root) closePopup();
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    initPopup();
    bindButtons();
    new MutationObserver(bindButtons).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
})();
